'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import WorkflowNavigation from '@/components/common/WorkflowNavigation';
import QuestionBlock from './QuestionBlock';
import { QuestionBlock as QuestionBlockType, Answer } from './types';

const questionBlocks: QuestionBlockType[] = [
  {
    blockNumber: 1,
    title: 'Alinhamento Estratégico',
    description: 'Avalie em que medida as prioridades do mandato estão traduzidas em objetivos operacionais claros, partilhados e acompanhados por toda a organização municipal.',
    questions: [
      {
        id: 'q1',
        text: 'As prioridades estratégicas do mandato estão formalmente definidas e são do conhecimento efetivo dos dirigentes e quadros técnicos responsáveis pela sua execução.',
      },
      {
        id: 'q2',
        text: 'Existem planos operacionais com metas mensuráveis e prazos definidos que traduzem as prioridades estratégicas em ações concretas, por serviço e por equipa.',
      },
      {
        id: 'q3',
        text: 'Os diferentes departamentos e serviços atuam de forma coordenada e alinhada na prossecução dos objetivos municipais, sem sobreposições nem lacunas de responsabilidade.',
      },
    ],
  },
  {
    blockNumber: 2,
    title: 'Reorganização e Processos',
    description: 'Avalie a adequação da estrutura orgânica e a eficiência dos processos internos face às exigências atuais de modernização, de clarificação de funções e de resposta eficaz ao cidadão.',
    questions: [
      {
        id: 'q4',
        text: 'A estrutura orgânica do município está adequada às necessidades atuais e não constitui um obstáculo à eficácia dos serviços nem à execução das prioridades do mandato.',
      },
      {
        id: 'q5',
        text: 'Não existem sobreposições de competências, duplicação de funções ou indefinição de responsabilidades que comprometam a eficiência operacional e a prestação de contas.',
        reverseScored: true,
      },
      {
        id: 'q6',
        text: 'Os processos internos críticos estão documentados, são do conhecimento dos colaboradores e são objeto de revisão e melhoria regulares, com responsáveis identificados.',
      },
    ],
  },
  {
    blockNumber: 3,
    title: 'Liderança e Gestão de Pessoas',
    description: 'Avalie a qualidade da liderança intermédia, a clarificação de funções e a capacidade de gestão estratégica dos recursos humanos municipais orientada para o desempenho.',
    questions: [
      {
        id: 'q7',
        text: 'A liderança de topo demonstra envolvimento ativo e consistente nos processos de reorganização interna e de modernização organizacional, com decisões tomadas com base em informação de qualidade.',
      },
      {
        id: 'q8',
        text: 'As funções e responsabilidades dos dirigentes intermédios estão claramente definidas, formalizadas e são exercidas com autonomia e responsabilização efetivas.',
      },
      {
        id: 'q9',
        text: 'Existe uma política de gestão estratégica de recursos humanos orientada para o desempenho, com avaliação regular, desenvolvimento de competências e retenção de talento.',
      },
    ],
  },
  {
    blockNumber: 4,
    title: 'Execução e Monitorização',
    description: 'Avalie a capacidade institucional de implementar decisões, cumprir compromissos, monitorizar resultados e corrigir desvios com agilidade e responsabilização.',
    questions: [
      {
        id: 'q10',
        text: 'O município dispõe de mecanismos eficazes de acompanhamento e avaliação do desempenho organizacional, com indicadores atualizados, acessíveis e utilizados na tomada de decisão.',
      },
      {
        id: 'q11',
        text: 'As decisões de gestão são suportadas por informação de qualidade e os desvios face aos objetivos são identificados, comunicados e corrigidos de forma atempada e estruturada.',
      },
      {
        id: 'q12',
        text: 'O município tem capacidade institucional comprovada para implementar projetos de melhoria e mudança organizacional dentro dos prazos e com os recursos estabelecidos.',
      },
    ],
  },
];

export default function QuestionnaireInteractive() {
  const router = useRouter();
  const [currentBlock, setCurrentBlock] = useState(0);
  const [answers, setAnswers] = useState<Answer>({});
  const [showValidation, setShowValidation] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const block = questionBlocks[currentBlock];
  const totalBlocks = questionBlocks.length;
  const totalQuestions = totalBlocks * 3;
  const answeredCount = Object.keys(answers).length;
  const completionPercentage = Math.round((answeredCount / totalQuestions) * 100);

  const blockAnswered = block.questions.every((q) => answers[q.id] !== undefined);

  useEffect(() => {
    const saved = sessionStorage.getItem('questionnaire_answers');
    const savedBlock = sessionStorage.getItem('questionnaire_block');
    if (saved) setAnswers(JSON.parse(saved));
    if (savedBlock) setCurrentBlock(parseInt(savedBlock, 10));
  }, []);

  const handleAnswer = (questionId: string, value: number) => {
    const updated = { ...answers, [questionId]: value };
    setAnswers(updated);
    sessionStorage.setItem('questionnaire_answers', JSON.stringify(updated));
    setShowValidation(false);
  };

  const handleNext = () => {
    if (!blockAnswered) {
      setShowValidation(true);
      return;
    }
    setShowValidation(false);
    if (currentBlock < totalBlocks - 1) {
      setIsTransitioning(true);
      setTimeout(() => {
        const next = currentBlock + 1;
        setCurrentBlock(next);
        sessionStorage.setItem('questionnaire_block', String(next));
        setIsTransitioning(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 200);
    } else {
      const scores = calculateScores(answers);
      sessionStorage.setItem('questionnaire_scores', JSON.stringify(scores));
      router.push('/lead-capture-form');
    }
  };

  const handleBack = () => {
    if (currentBlock > 0) {
      setIsTransitioning(true);
      setTimeout(() => {
        const prev = currentBlock - 1;
        setCurrentBlock(prev);
        sessionStorage.setItem('questionnaire_block', String(prev));
        setIsTransitioning(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 200);
    } else {
      router.push('/landing-page');
    }
  };

  const isLastBlock = currentBlock === totalBlocks - 1;

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#F5F5F5' }}>
      {/* Header */}
      <header className="w-full" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        {/* Top bar */}
        <div style={{ backgroundColor: '#272727' }}>
          <div className="max-w-6xl mx-auto px-6 py-2 flex items-center justify-between">
            <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.72rem', color: 'rgba(255,255,255,0.6)', letterSpacing: '0.05em' }}>
              Diagnóstico de Capacidade Organizacional Municipal
            </span>
            <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.72rem', color: 'rgba(255,255,255,0.45)' }}>
              Confidencial e Gratuito
            </span>
          </div>
        </div>
        {/* Main nav */}
        <div className="bg-white border-b border-gray-100">
          <div className="max-w-6xl mx-auto px-6 py-0 flex items-center justify-between">
            <Link href="/landing-page" className="flex items-center py-3 focus-visible:outline-none" aria-label="GestPub — Gestão Pública e Consultoria Autárquica — Página inicial">
              <img
                src="/assets/images/6-1773621431219.png"
                alt="GestPub — Gestão Pública e Consultoria Autárquica"
                style={{ height: 40, width: 'auto', display: 'block', flexShrink: 0, mixBlendMode: 'multiply' }}
              />
            </Link>
            {/* Progress indicator */}
            <div className="flex items-center gap-3">
              <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.75rem', color: '#6B7280' }}>
                {answeredCount} / {totalQuestions} questões
              </span>
              <div className="w-32 h-1.5 overflow-hidden" style={{ borderRadius: '9999px', backgroundColor: '#F3F4F6' }}>
                <div
                  className="h-full transition-all duration-300"
                  style={{ width: `${completionPercentage}%`, backgroundColor: '#009688', borderRadius: '9999px' }}
                />
              </div>
              <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.75rem', color: '#6B7280', fontWeight: 500 }}>
                {completionPercentage}%
              </span>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 py-10 px-4" style={{ backgroundColor: '#F5F5F5' }}>
        <div className="max-w-3xl mx-auto">
          {/* Block pills — step indicator */}
          <div className="flex gap-2 mb-8 overflow-x-auto pb-1">
            {questionBlocks.map((b, idx) => {
              const blockComplete = b.questions.every((q) => answers[q.id] !== undefined);
              const isCurrent = idx === currentBlock;
              return (
                <div
                  key={b.blockNumber}
                  className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs transition-all duration-200"
                  style={{
                    borderRadius: '5px',
                    fontFamily: 'DM Sans, sans-serif',
                    fontWeight: 500,
                    backgroundColor: isCurrent ? '#272727' : blockComplete ? '#F3F4F6' : '#FFFFFF',
                    color: isCurrent ? '#FFFFFF' : blockComplete ? '#374151' : '#6B7280',
                    border: isCurrent ? '1px solid #272727' : blockComplete ? '1px solid #D1D5DB' : '1px solid #E5E7EB',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
                  }}
                >
                  {blockComplete && !isCurrent && (
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                      <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                  <span>{b.title}</span>
                </div>
              );
            })}
          </div>

          {/* Question block */}
          <div className={`transition-opacity duration-200 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>
            <QuestionBlock
              block={block}
              answers={answers}
              onAnswer={handleAnswer}
              showValidation={showValidation}
            />
          </div>

          {/* Validation message */}
          {showValidation && !blockAnswered && (
            <div
              className="mt-4 p-3"
              style={{ borderRadius: '5px', backgroundColor: '#FEF2F2', border: '1px solid #FECACA' }}
              role="alert"
            >
              <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.875rem', color: '#DC2626' }}>
                Por favor, responda a todas as questões deste bloco antes de continuar.
              </p>
            </div>
          )}

          {/* Navigation */}
          <WorkflowNavigation
            currentPath="/multi-step-questionnaire"
            canProceed={true}
            onNext={handleNext}
            onBack={handleBack}
            nextLabel={isLastBlock ? 'Continuar para Contacto' : 'Próximo Bloco'}
            backLabel={currentBlock === 0 ? 'Voltar ao Início' : 'Bloco Anterior'}
          />

          <p
            className="text-center mt-4"
            style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.75rem', color: '#9CA3AF' }}
          >
            Bloco {currentBlock + 1} de {totalBlocks} &bull; {answeredCount} de {totalQuestions} questões respondidas
          </p>
        </div>
      </main>

      {/* Minimal footer */}
      <footer style={{ backgroundColor: '#272727' }}>
        <div className="max-w-6xl mx-auto px-6 py-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.78rem', color: 'rgba(255,255,255,0.35)' }}>
            &copy; {new Date().getFullYear()} GestPub – Gestão Pública e Consultoria Autárquica. Todos os direitos reservados.
          </p>
          <div className="flex gap-6">
            {[
              { label: 'Política de Privacidade', href: '/politica-de-privacidade' },
              { label: 'RGPD', href: '/rgpd' },
            ]?.map((link) => (
              <Link
                key={link?.label}
                href={link?.href}
                className="transition-colors duration-200"
                style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.78rem', color: 'rgba(255,255,255,0.35)' }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#FFFFFF')}
                onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.35)')}
              >
                {link?.label}
              </Link>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}

function calculateScores(answers: Answer): Record<string, number> {
  const blockIds = [
    ['q1', 'q2', 'q3'],
    ['q4', 'q5', 'q6'],
    ['q7', 'q8', 'q9'],
    ['q10', 'q11', 'q12'],
  ];
  const blockNames = ['estrategia', 'estrutura', 'pessoas', 'execucao'];
  const reverseScored = ['q5'];

  const scores: Record<string, number> = {};
  blockIds.forEach((ids, idx) => {
    const total = ids.reduce((sum, id) => {
      const val = answers[id] ?? 3;
      return sum + (reverseScored.includes(id) ? 6 - val : val);
    }, 0);
    scores[blockNames[idx]] = Math.round((total / 15) * 100);
  });
  scores.total = Math.round(Object.values(scores).reduce((a, b) => a + b, 0) / 4);
  return scores;
}